// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Mention Resolution Utility
 *
 * Resolves @mention identifiers to TeamMember IDs.
 */

import type { ParsedMention } from './mentionParser'
import type { DiscussionMention } from '../types'

/**
 * Lookup entry for agent/user resolution.
 *
 * `teamMemberId` may be empty string for entries that resolved via a user's
 * email handle but are not linked to a teammember row (rare; user-only mention).
 * `userId` is set for human users when known; agents leave it undefined.
 */
export interface AgentLookupEntry {
  teamMemberId: string
  userId?: string
  isAgent: boolean
}

/**
 * Resolve parsed mentions to full DiscussionMention objects.
 *
 * Hybrid shape: stores BOTH `identifier` (the literal `@handle` typed) and
 * `teamMemberId` (resolved at write time). The query layer matches either,
 * so a slug rotation does not invalidate older mentions whose `teamMemberId`
 * still points at the original row.
 *
 * @param parsed - Array of parsed mentions from mentionParser
 * @param agentLookup - Map of identifier to TeamMember/User info
 * @returns Array of resolved DiscussionMention objects
 *
 * @example
 * ```typescript
 * const lookup = new Map([
 *   ['viktor-petrov', { teamMemberId: 'team_abc', isAgent: true }]
 * ]);
 * const resolved = resolveMentions(parsed, lookup);
 * ```
 */
export function resolveMentions(
  parsed: ParsedMention[],
  agentLookup: Map<string, AgentLookupEntry>
): DiscussionMention[] {
  return parsed.map(p => {
    const entry = agentLookup.get(p.identifier)

    const mention: DiscussionMention = {
      // If entry exists, check isAgent; if no entry, default to 'agent'
      type: entry ? (entry.isAgent ? 'agent' : 'user') : 'agent',
      identifier: p.identifier,
      position: p.position,
    }

    // Treat empty-string teamMemberId as "no resolved teammember" (user-only entry)
    if (entry?.teamMemberId) {
      mention.teamMemberId = entry.teamMemberId
    }
    if (entry?.userId) {
      mention.userId = entry.userId
    }

    return mention
  })
}
